import { YarMark } from '../../../shell/yar/YarMark.js';
import { SAMPLE_ANSWERS, SCOPE_LABELS, SUGGESTED_PROMPTS } from './sample.js';
import type { ScopeKey } from './types.js';

export interface EmptyStateProps {
  readonly firstName: string;
  readonly granted: readonly ScopeKey[];
  readonly onPick: (text: string) => void;
  readonly disabled?: boolean;
  readonly live?: boolean;
}

const LIVE_PROMPTS: readonly {
  id: string;
  tag: string;
  text: string;
  requires: readonly ScopeKey[];
}[] = [
  {
    id: 'general',
    tag: 'پرسش عمومی',
    text: 'تفاوت سود و جریان نقدی را با یک مثال توضیح بده.',
    requires: [],
  },
  {
    id: 'overview',
    tag: 'نمای کلی',
    text: 'وضعیت شرکت را بر اساس اطلاعات ثبت‌شده خلاصه کن.',
    requires: [],
  },
  {
    id: 'sales',
    tag: 'فروش',
    text: 'روند فروش شش ماه اخیر را با نمودار نشان بده.',
    requires: ['sales'],
  },
  {
    id: 'finance',
    tag: 'مالی',
    text: 'مطالبات باز و بدهی‌های ما را جداگانه در جدول نشان بده.',
    requires: ['accounting'],
  },
  {
    id: 'inventory',
    tag: 'انبار',
    text: 'موجودی ثبت‌شدهٔ انبار را در جدول نشان بده.',
    requires: ['inventory'],
  },
  {
    id: 'projects',
    tag: 'پروژه',
    text: 'کارهای عقب‌افتاده را با میزان تأخیر در جدول نشان بده.',
    requires: ['projects'],
  },
];

/** Frame A — greeting + six suggested-prompt cards (cards needing a module without access are inert). */
export function EmptyState({
  firstName,
  granted,
  onPick,
  disabled = false,
  live = false,
}: EmptyStateProps) {
  const prompts = live
    ? LIVE_PROMPTS
    : SUGGESTED_PROMPTS.map((prompt) => ({
        ...prompt,
        requires: SAMPLE_ANSWERS[prompt.answerId]?.requires ?? [],
      }));
  return (
    <div className="ai-empty">
      <div className="ai-empty__hero">
        <span className="ai-empty__mark" aria-hidden="true">
          <YarMark size={48} />
        </span>
        <h2 className="ai-empty__title">سلام {firstName}، چه چیزی را بررسی کنیم؟</h2>
        <p className="ai-empty__text">
          {live
            ? 'سؤال خود را بپرسید یا تحلیل شرکت را با جدول و نمودار بخواهید. اطلاعات شرکت در محدودهٔ دسترسی شما بررسی می‌شود.'
            : 'سؤال تحلیلی خود را دربارهٔ دادهٔ سازمانتان بنویسید. دستیار فقط می‌خواند و چیزی را تغییر نمی‌دهد.'}
        </p>
      </div>
      <div className="ai-suggest">
        {prompts.map((prompt) => {
          const requires = prompt.requires;
          const missing = requires.filter((key) => !granted.includes(key));
          const blocked = missing.length > 0;
          return (
            <button
              key={prompt.id}
              type="button"
              className="sk-card ai-suggest__card"
              disabled={disabled || blocked}
              onClick={() => onPick(prompt.text)}
              title={
                blocked
                  ? `بدون دسترسی به ${missing.map((key) => SCOPE_LABELS[key]).join('، ')}`
                  : undefined
              }
            >
              <span className="ai-suggest__tag">{prompt.tag}</span>
              <span className="ai-suggest__text">{prompt.text}</span>
              {blocked ? (
                <span className="ai-suggest__lock">
                  بدون دسترسی: {missing.map((key) => SCOPE_LABELS[key]).join('، ')}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
